import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

export function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleGoogleCallback } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use useRef to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        const result = await handleGoogleCallback(sessionId);
        
        if (result.success) {
          navigate('/dashboard', { replace: true, state: { user: result.user } });
        } else {
          navigate('/login', { replace: true, state: { error: result.error } });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    processCallback();
  }, [location.hash, handleGoogleCallback, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-[#0055FF] mx-auto" />
        <p className="mt-4 text-[#4B5563]">Connexion en cours...</p>
      </div>
    </div>
  );
}

export function ProtectedRoute({ children, requireCSPS, requireOPC, adminOnly }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && user === false) {
      navigate('/login', { replace: true, state: { from: location.pathname } });
    }
  }, [user, loading, navigate, location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <Loader2 className="w-10 h-10 animate-spin text-[#0055FF]" />
      </div>
    );
  }

  if (user === false) {
    return null;
  }

  // Feature gating par profil et role
  if (adminOnly && user?.role !== 'admin') {
    return <ProfileGateMessage title="Accès administrateur" message="Cette page est réservée aux administrateurs." />;
  }
  const profile = user?.profile_type || 'opc_csps';
  if (requireCSPS && profile === 'opc') {
    return <ProfileGateMessage title="Fonctionnalité CSPS" message="Votre abonnement actuel est OPC uniquement. Passez au pack CSPS ou OPC+CSPS pour accéder à cette section." link="/subscription" linkLabel="Voir les abonnements" />;
  }
  if (requireOPC && profile === 'csps') {
    return <ProfileGateMessage title="Fonctionnalité OPC" message="Votre abonnement actuel est CSPS uniquement. Passez au pack OPC ou OPC+CSPS pour accéder à cette section." link="/subscription" linkLabel="Voir les abonnements" />;
  }

  return children;
}

function ProfileGateMessage({ title, message, link, linkLabel }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-6">
      <div className="max-w-md w-full bg-white rounded-2xl border shadow-sm p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
          <svg viewBox="0 0 24 24" fill="none" className="w-7 h-7 text-amber-600"><path stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M4.93 19h14.14a2 2 0 001.74-3l-7.07-12a2 2 0 00-3.48 0L3.2 16a2 2 0 001.74 3z"/></svg>
        </div>
        <h2 className="text-lg font-bold text-[#111827] mb-2">{title}</h2>
        <p className="text-sm text-[#4B5563] mb-4">{message}</p>
        {link && (
          <a href={link} className="inline-block px-5 py-2.5 rounded-lg bg-[#0055FF] hover:bg-blue-700 text-white text-sm font-semibold">
            {linkLabel || 'Continuer'}
          </a>
        )}
      </div>
    </div>
  );
}
